import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {DataService} from '../../../../Services/data.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EntityTypeModel } from '../../../../DataModels/entity-type-model';
import { ConfirmDialogService } from '../../../../Services/confirm-dialog.service';
import { BsToastService } from '../../../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';
import {NavigationService} from '../../../../Services/navigation.service';
import {CanComponentDeactivate} from '../../../../Interfaces/unsaved-changes.interface';
import {UnsavedChangesComponent} from '../../../unsaved-changes/unsaved-changes/unsaved-changes.component';

@Component({
  selector: 'app-entity-type-form',
  templateUrl: './entity-type-form.component.html',
  styleUrls: ['./entity-type-form.component.css'],
  standalone: false
})
export class EntityTypeFormComponent implements OnInit, AfterViewInit, CanComponentDeactivate {
  @ViewChild('unsavedChanges') unsavedChanges!: UnsavedChangesComponent;
  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: false,
  };

  entityType = new EntityTypeModel();
  entityTypeForm: UntypedFormGroup;
  entityTypeID;
  submitted = false;

  // Unsaved-changes tracking
  originalFormData: any = {};

  constructor(
      private table: DataService,
    private notification: BsToastService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private confirmDialogService: ConfirmDialogService,
      private titleService: Title,
      private nav: NavigationService
  ) {
    this.createForm();
    this.entityTypeID = this.route.snapshot.params.id;
  }

  get f() {
    return this.entityTypeForm.controls;
  }

  createForm() {
    this.entityTypeForm = this.formBuilder.group({
      id: new UntypedFormControl(null),
      name: new UntypedFormControl(''),
      active: new UntypedFormControl(true),
    });
  }

  ngOnInit() {
    this.titleService.setTitle($localize`:Entity Type @@entityType:Entity Type`);

    if (this.entityTypeID) {
      this.loadEntityType();
      this.buttonConfig.reset = false;
    }
  }

  ngAfterViewInit() {
    // Establish baseline for new records after view initializes
    if (!this.entityTypeID) {
      this.originalFormData = JSON.parse(JSON.stringify(this.entityTypeForm.getRawValue()));
    }
  }

  private loadEntityType() {
    this.table.getData('entity_type', this.entityTypeID).subscribe({
      next: (result: any) => {
        // Handle both wrapped { Data: T } and raw T responses
        const data = (result && result.Data !== undefined) ? result.Data : result;
        this.entityType = data as EntityTypeModel;
        this.entityTypeForm.patchValue(this.entityType);
        // Establish baseline for Unsaved Changes guard
        this.originalFormData = JSON.parse(JSON.stringify(this.entityTypeForm.getRawValue()));
      },
      error: (error: HttpErrorResponse) => {
        // this.notification.error($localize`:Error loading Entity Type @@errorLoadingEntityType:Error loading Entity Type`);
        console.log(
          error + ' - ' + $localize`:Error loading Entity Type @@errorLoadingEntityType:Error loading Entity Type`
        );
      }
    });
  }

  saveClose() {
    // Delegate to unified save handler
    this.onSave('saveClose');
  }

  saveNew() {
    // Trigger save for a new record and reset after async completion inside onSave('saveNew')
    this.onSave('saveNew');
  }

  deleteItem() {
    let retMsg = '';
    const messages: string[] = [
      $localize`:Are you sure you want to delete entity type? @@areYouSureYouWantToDeleteEntityType:Are you sure you want to delete entity type?`,
      this.entityType.name,
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.table.deleteDataById('entity_type', this.entityTypeID).subscribe({
          next: (result) => {
            retMsg = result as any;
            // this.notification.success($localize`:Deleted @@deleted:Deleted`);
          },
          error: (error: HttpErrorResponse) => {
            // this.notification.error($localize`:Error @@error:Error`);
            console.log(error);
          },
          complete: () => {
            this.onClose();
          }
        });
      },
      () => {
        this.notification.showSuccessToast($localize`:Delete Canceled @@Delete Canceled:Delete Canceled`);
      }
    );
  }

  resetForm() {
    this.resetToDefaults();
    // After an explicit reset, treat current state as clean
    this.originalFormData = JSON.parse(JSON.stringify(this.entityTypeForm.getRawValue()));
    if (this.unsavedChanges) {
      this.unsavedChanges.resetChanges();
    }
  }

  onClose() {
    const fallback = '/admin/entity-type-list';
    this.nav.backOr(fallback);
  }

  private resetToDefaults(): void {
    // Reset the form to known default values and clear component state for a fresh entry
    this.entityTypeForm.reset({
      id: null,
      name: '',
      active: true,
    });

    // Reset backing model and id so UI behaves like a new record
    this.entityType = new EntityTypeModel();
    this.entityType.active = true;
    this.entityTypeID = null;
  }

  // Unified save handler with modes
  onSave(mode: 'save' | 'saveClose' | 'saveNew') {
    this.submitted = true;
    if (this.entityTypeForm.invalid) {
      this.notification.showWarningToast($localize`:Form Errors! @@formErrors:Form Errors!`);
      this.submitted = false;
      return false;
    }

    // Normalize defaults for new records
    if (this.entityTypeForm.get('id').value === '' || this.entityTypeForm.get('id').value === null) {
      this.entityTypeForm.patchValue({ id: null, active: true });
    }

    this.entityType = Object.assign({}, this.entityTypeForm.value);

    if (this.entityTypeID) {
      if (this.entityTypeForm.invalid === false) {
        this.table.patch('entity_type', this.entityTypeID, this.entityType).then(
          () => {
            // Reset baseline to saved state
            this.originalFormData = JSON.parse(JSON.stringify(this.entityTypeForm.getRawValue()));
            if (this.unsavedChanges) {
              this.unsavedChanges.resetChanges();
            }
            if (mode === 'saveClose') {
              this.onClose();
            } else if (mode === 'saveNew') {
              // Prepare a fresh form for a new record
              this.entityTypeID = undefined;
              this.entityTypeForm.reset();
              // After save-new, consider the fresh form as clean
              this.originalFormData = JSON.parse(JSON.stringify(this.entityTypeForm.getRawValue()));
              // Navigate to clean form URL (no id)
              this.router.navigate(['/admin/entity-type-form'], {
                replaceUrl: true
              }).then(() => {
              });
              // Ensure Close returns to list once after Save New
              this.nav.forceNextBackToFallback();
            } else {
              // mode === 'save' -> keep values on screen
              this.originalFormData = JSON.parse(JSON.stringify(this.entityTypeForm.getRawValue()));
            }
          },
          (error: HttpErrorResponse) => {
            console.log(error);
          }
        ).finally(() => {
          this.submitted = false;
        });
      }
    } else {
      this.table.postWithPromise('entity_type', this.entityType).then((res) => {
        let apiReturn: any = null;
        if (typeof res === 'object') {
          apiReturn = JSON.parse(JSON.stringify(res));
        }
        this.entityTypeID = apiReturn?.id;
        if (mode === 'saveNew') {
          // Start a brand-new blank record
          this.entityTypeID = undefined;
          this.entityTypeForm.reset();
          this.originalFormData = JSON.parse(JSON.stringify(this.entityTypeForm.getRawValue()));
          if (this.unsavedChanges) {
            this.unsavedChanges.resetChanges();
          }
          this.router.navigate(['/admin/entity-type-form'], {
            replaceUrl: true
          }).then(() => {
          });
          this.nav.forceNextBackToFallback();
        } else if (mode === 'saveClose') {
          // Keep values, then close
          this.originalFormData = JSON.parse(JSON.stringify(this.entityTypeForm.getRawValue()));
          if (this.unsavedChanges) {
            this.unsavedChanges.resetChanges();
          }
          this.onClose();
        } else { // mode === 'save'
          // Stay on form with saved values and new ID
          this.originalFormData = JSON.parse(JSON.stringify(this.entityTypeForm.getRawValue()));
          if (this.unsavedChanges) {
            this.unsavedChanges.resetChanges();
          }
        }
      }).catch((error: HttpErrorResponse) => {
        console.log(error);
      }).finally(() => {
        this.submitted = false;
      });
    }

    return true;
  }

  // Implementation for UnsavedChangesGuard
  checkForChanges(): boolean {
    try {
      const current = this.entityTypeForm?.getRawValue?.() ?? {};
      return JSON.stringify(this.originalFormData) !== JSON.stringify(current);
    } catch {
      return false;
    }
  }
}
